import React, { useState } from 'react';
import { X, MessageCircle, CheckSquare, Cloud, AlignLeft, Star, Brain, ListOrdered, Disc3, ArrowUpDown, LayoutGrid, SlidersHorizontal, ImageIcon, Hash, MousePointerClick, CircleDollarSign, Smile, Shuffle, TrendingUp, Clock, Target, MessageSquare, Zap, Pencil, Check, Ticket, Cherry } from 'lucide-react';
import Button from './ui/Button';
import styles from './ActivityTypeModal.module.css';

const ACTIVITY_TYPES = [
    {
        id: 'multiple-choice',
        label: 'Multiple Choice',
        icon: <CheckSquare size={20} />,
        description: 'Poll your audience with options to vote on',
        color: 'var(--cat-violet)',
    },
    {
        id: 'audience-qa',
        label: 'Audience Q&A',
        icon: <MessageCircle size={20} />,
        description: 'Let your audience ask questions in real-time',
        color: 'var(--cat-blue)',
    },
    {
        id: 'word-cloud',
        label: 'Word Cloud',
        icon: <Cloud size={20} />,
        description: 'Collect words that appear as a live cloud',
        color: 'var(--cat-cyan)',
    },
    {
        id: 'open-text',
        label: 'Open Text',
        icon: <AlignLeft size={20} />,
        description: 'Gather free-form text responses from your audience',
        color: 'var(--cat-green)',
    },
    {
        id: 'quiz',
        label: 'Quiz',
        icon: <Brain size={20} />,
        description: 'Test knowledge with a live scored quiz',
        color: 'var(--cat-rose)',
    },
    {
        id: 'rating',
        label: 'Rating',
        icon: <Star size={20} />,
        description: 'Ask your audience to rate something on a scale',
        color: 'var(--cat-amber)',
    },
    {
        id: 'ranking',
        label: 'Ranking',
        icon: <ListOrdered size={20} />,
        description: 'Let your audience rank items by preference',
        color: 'var(--cat-orange)',
    },
    {
        id: 'rating-scale',
        label: 'Rating Scale',
        icon: <SlidersHorizontal size={20} />,
        description: 'Rate on a numeric scale (e.g. 1–10)',
        color: 'var(--cat-teal)',
    },
    {
        id: 'correct-order',
        label: 'Correct Order',
        icon: <ArrowUpDown size={20} />,
        description: 'Have participants arrange items in the right sequence',
        color: 'var(--cat-teal)',
    },
    {
        id: 'categories',
        label: 'Categories',
        icon: <LayoutGrid size={20} />,
        description: 'Group items into their correct categories',
        color: 'var(--cat-violet)',
    },
    {
        id: 'spinner-wheel',
        label: 'Spinner Wheel',
        icon: <Disc3 size={20} />,
        description: 'Spin a wheel to randomly pick from options',
        color: 'var(--cat-pink)',
    },
    {
        id: 'image-choice',
        label: 'Image Choice',
        icon: <ImageIcon size={20} />,
        description: 'Let participants vote by selecting an image',
        color: 'var(--cat-indigo)',
    },
    {
        id: 'slider',
        label: 'Slider',
        icon: <SlidersHorizontal size={20} />,
        description: 'Collect a value on a draggable slider range',
        color: 'var(--cat-teal)',
    },
    {
        id: 'numeric-input',
        label: 'Numeric Input',
        icon: <Hash size={20} />,
        description: 'Participants enter a number as their response',
        color: 'var(--cat-violet)',
    },
    {
        id: 'click-image',
        label: 'Click on Image',
        icon: <MousePointerClick size={20} />,
        description: 'Participants click locations on an image',
        color: 'var(--cat-cyan)',
    },
    {
        id: 'budget-allocation',
        label: 'Budget Allocation',
        icon: <CircleDollarSign size={20} />,
        description: 'Distribute a fixed budget across options',
        color: 'var(--cat-amber)',
    },
    {
        id: 'reaction',
        label: 'Reaction',
        icon: <Smile size={20} />,
        description: 'React to a prompt with an emoji',
        color: 'var(--cat-pink)',
    },
    {
        id: 'pair-matching',
        label: 'Pair Matching',
        icon: <Shuffle size={20} />,
        description: 'Match left and right column items',
        color: 'var(--cat-violet)',
    },
    {
        id: 'prediction',
        label: 'Prediction',
        icon: <TrendingUp size={20} />,
        description: 'Predict an outcome before it happens',
        color: 'var(--cat-orange)',
    },
    {
        id: 'timeline-order',
        label: 'Timeline Ordering',
        icon: <Clock size={20} />,
        description: 'Arrange events in chronological order',
        color: 'var(--cat-teal)',
    },
    {
        id: 'hotspot',
        label: 'Hotspot',
        icon: <Target size={20} />,
        description: 'Click predefined areas on an image',
        color: 'var(--cat-rose)',
    },
    {
        id: 'debate',
        label: 'Debate',
        icon: <MessageSquare size={20} />,
        description: 'Choose a side and submit an argument',
        color: 'var(--cat-blue)',
    },
    {
        id: 'guess-number',
        label: 'Guess the Number',
        icon: <Hash size={20} />,
        description: 'Guess a hidden number — closest wins',
        color: 'var(--cat-orange)',
    },
    {
        id: 'speed-quiz',
        label: 'Speed Quiz',
        icon: <Zap size={20} />,
        description: 'Answer correctly — fastest response wins',
        color: 'var(--cat-rose)',
    },
    {
        id: 'drawing',
        label: 'Drawing',
        icon: <Pencil size={20} />,
        description: 'Participants submit free-hand drawings',
        color: 'var(--cat-violet)',
    },
    {
        id: 'scratch-card',
        label: 'Scratch Card',
        icon: <Ticket size={20} />,
        description: 'Each participant scratches to reveal a random prize',
        color: 'var(--cat-amber)',
    },
    {
        id: 'slot-machine',
        label: 'Slot Machine',
        icon: <Cherry size={20} />,
        description: 'Spin three reels — everyone lands on a random prize',
        color: 'var(--cat-orange)',
    },
];

const ActivityTypeModal = ({ isOpen, onClose, event, onSelect }) => {
    const [selected, setSelected] = useState(null);

    const handleConfirm = () => {
        if (!selected) return;
        onSelect(selected);
        setSelected(null);
        onClose();
    };

    const handleClose = () => {
        setSelected(null);
        onClose();
    };

    if (!isOpen || !event) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerText}>
                        <h2 className={styles.title}>Choose Activity Type</h2>
                        <p className={styles.subtitle}>
                            <span className={styles.eventBadge}>{event.name}</span>
                            &nbsp;· What kind of activity do you want to add?
                        </p>
                    </div>
                    <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
                        <X size={20} />
                    </button>
                </div>
                <p className={styles.hint}>Start with one interaction, launch it live, then add follow-up interactions as needed.</p>

                {/* Grid */}
                <div className={styles.grid}>
                    {ACTIVITY_TYPES.map((type) => (
                        <button
                            key={type.id}
                            className={`${styles.card} ${selected?.id === type.id ? styles.cardSelected : ''}`}
                            onClick={() => setSelected(type)}
                            aria-pressed={selected?.id === type.id}
                            style={{
                                '--card-color': type.color,
                            }}
                        >
                            <div className={styles.cardIcon}>{type.icon}</div>
                            <div className={styles.cardText}>
                                <div className={styles.cardLabel}>{type.label}</div>
                                <div className={styles.cardDesc}>{type.description}</div>
                            </div>
                            {selected?.id === type.id && (
                                <div className={styles.checkmark}>
                                    <Check size={11} strokeWidth={3} aria-hidden />
                                </div>
                            )}
                        </button>
                    ))}
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <Button variant="secondary" size="sm" className={styles.cancelBtn} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        className={styles.confirmBtn}
                        onClick={handleConfirm}
                        disabled={!selected}
                    >
                        {selected ? `Add ${selected.label}` : 'Select an activity'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ActivityTypeModal;
